import request from "../_helpers/request";

function toInt(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function getCurrentLangId() {
  return String(
    typeof localStorage !== "undefined"
      ? localStorage.getItem("currentLangId") || "936"
      : "936"
  );
}

/** 与 salesLead/nameCard 等页一致：按 currentLangId 取简体/繁体/英文显示名 */
export function resolveLocalizedPersonName(record, langId = getCurrentLangId()) {
  const sc = String(record?.personNameSc ?? record?.PersonNameSc ?? "").trim();
  const en = String(record?.personNameEn ?? record?.PersonNameEn ?? "").trim();
  const tc = String(record?.personNameTc ?? record?.PersonNameTc ?? "").trim();
  const fallback = String(
    record?.personName ??
      record?.PersonName ??
      record?.displayName ??
      record?.DisplayName ??
      record?.userName ??
      record?.UserName ??
      ""
  ).trim();

  const lid = String(langId || "936");
  if (lid === "1252") {
    return en || sc || tc || fallback;
  }
  if (lid === "950") {
    return tc || sc || en || fallback;
  }
  return sc || tc || en || fallback;
}

/**
 * 头像占位首字：跳过开头标点/符号（如 [、(、*），取第一个字母、数字或中文。
 */
export function resolveDisplayInitial(name, fallback = "U") {
  const text = String(name || "").trim();
  if (!text) return fallback;

  try {
    const match = text.match(/[\p{L}\p{N}]/u);
    if (match && match[0]) {
      return match[0].toUpperCase();
    }
  } catch (e) {
    const match = text.match(/[A-Za-z0-9\u4e00-\u9fff]/);
    if (match && match[0]) {
      return match[0].toUpperCase();
    }
  }

  return fallback;
}

/** 备注时间展示：不显示秒（如 2026-05-15 11:25:46 → 2026-05-15 11:25） */
export function formatMessageDateTime(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  const withoutSeconds = text.match(
    /^(\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}):\d{2}(?:\.\d+)?/
  );
  if (withoutSeconds) {
    return withoutSeconds[1].replace("T", " ");
  }

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
  }

  return text.replace(/:\d{2}(?:\.\d+)?$/, "");
}

function normalizeMessage(raw = {}, langId = getCurrentLangId()) {
  const personNameSc = raw.personNameSc ?? raw.PersonNameSc ?? "";
  const personNameEn = raw.personNameEn ?? raw.PersonNameEn ?? "";
  const personNameTc = raw.personNameTc ?? raw.PersonNameTc ?? "";
  const displayName = raw.displayName ?? raw.DisplayName ?? "";
  const userName = raw.userName ?? raw.UserName ?? "";
  const base = {
    personNameSc,
    personNameEn,
    personNameTc,
    displayName,
    userName
  };

  return {
    id: toInt(raw.id ?? raw.Id),
    parentId: toInt(raw.parentId ?? raw.ParentId),
    rootId: toInt(raw.rootId ?? raw.RootId),
    level: toInt(raw.level ?? raw.Level),
    type: raw.type ?? raw.Type ?? "",
    formId: toInt(raw.formId ?? raw.FormId),
    bizType: raw.bizType ?? raw.BizType ?? "",
    bizId: toInt(raw.bizId ?? raw.BizId),
    remark: raw.remark ?? raw.Remark ?? "",
    accountId: toInt(raw.accountId ?? raw.AccountId),
    accountUserId: toInt(raw.accountUserId ?? raw.AccountUserId),
    createUser: raw.createUser ?? raw.CreateUser ?? "",
    updateUser: raw.updateUser ?? raw.UpdateUser ?? "",
    createTime: raw.createTime ?? raw.CreateTime ?? "",
    updateTime: raw.updateTime ?? raw.UpdateTime ?? "",
    isDelete: Boolean(raw.isDelete ?? raw.IsDelete),
    userRole: raw.userRole ?? raw.UserRole ?? "",
    personNameSc,
    personNameEn,
    personNameTc,
    displayName,
    userName,
    personName: resolveLocalizedPersonName(
      { ...base, personName: raw.personName ?? raw.PersonName ?? "" },
      langId
    ),
    userLogo: raw.userLogo ?? raw.UserLogo ?? "",
    children: []
  };
}

function buildThreadTree(rawList = [], langId = getCurrentLangId()) {
  const nodes = rawList.map((raw) => normalizeMessage(raw, langId));
  const map = new Map();
  const roots = [];

  nodes.forEach((node) => {
    map.set(node.id, node);
  });

  nodes.forEach((node) => {
    if (node.parentId > 0 && map.has(node.parentId)) {
      map.get(node.parentId).children.push(node);
      return;
    }
    roots.push(node);
  });

  const sortByCreateTime = (items) => {
    items.sort((a, b) => {
      return new Date(a.createTime).getTime() - new Date(b.createTime).getTime();
    });
    items.forEach((item) => sortByCreateTime(item.children));
  };

  sortByCreateTime(roots);
  return roots;
}

export const messageBoardService = {
  getTypes,
  listMessages,
  listThreadTree,
  getCurrentActor,
  createMessage,
  removeMessage,
  buildThreadTree,
  resolveLocalizedPersonName,
  resolveDisplayInitial,
  formatMessageDateTime,
  getCurrentLangId
};

function getTypes(query = {}) {
  return request({
    url: "MessageBoard/GetTypes",
    method: "get",
    params: query
  });
}

function listMessages(query = {}) {
  return request({
    url: "MessageBoard/List",
    method: "get",
    params: query
  });
}

function listThreadTree(query = {}) {
  return listMessages(query).then((res) => {
    const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    return buildThreadTree(list);
  });
}

function getCurrentActor() {
  return request({
    url: "MessageBoard/GetCurrentActor",
    method: "get"
  });
}

function createMessage(payload = {}) {
  return request.post("MessageBoard/Create", payload);
}

function removeMessage(payload = {}) {
  return request.post("MessageBoard/Delete", payload);
}
